import {
  getCustomCellBinding,
  getCustomTableCellValue
} from '../customSheetFields';
import {
  getValueAtPath,
  parseTokenPath,
  toInputValue
} from '../documentStateHelpers';
import { getVariableCellMeta } from '../sheetLayoutHelpers';
import { getCustomEntityRecord, resolveVariableFieldBinding } from '../resolveVariableField';
import {
  getPrimaryVariableToken,
  isMixedVariableCell
} from '../variableTokenHelpers';
import {
  buildCheckboxIndexMap,
  getSheetCheckboxValue
} from '../sheetCheckboxHelpers';
import { coerceSheetValue } from './coerceSheetValue';

const asExpandedSheetList = expandedSheets => {
  if (Array.isArray(expandedSheets)) {
    return expandedSheets.filter(sheet => sheet?.name);
  }
  return expandedSheets?.name ? [expandedSheets] : [];
};

/**
 * Push editable / static inputs into HyperFormula for one expanded sheet grid.
 * Formula cells are left alone (already loaded + row-shifted).
 */
const syncOneSheetInputsIntoEngine = ({
  engine,
  expandedSheet,
  documentState,
  pickedInventory,
  appChoices,
  docSource
}) => {
  const sheetName = expandedSheet.name;
  const rows = Math.max(
    expandedSheet.rows || 0,
    expandedSheet.values?.length || 0,
    expandedSheet.formulas?.length || 0
  );
  const cols = Math.max(
    expandedSheet.cols || 0,
    ...(expandedSheet.values || []).map(r => (Array.isArray(r) ? r.length : 0)),
    1
  );

  const checkboxMap = buildCheckboxIndexMap(expandedSheet);

  for (let row0 = 0; row0 < rows; row0++) {
    for (let col0 = 0; col0 < cols; col0++) {
      if (engine.hasFormula(row0, col0, sheetName)) continue;

      const row1 = row0 + 1;
      const col1 = col0 + 1;

      const customBinding = getCustomCellBinding(
        sheetName,
        row1,
        col1,
        documentState,
        appChoices
      );

      if (customBinding) {
        engine.setCellValue(
          row0,
          col0,
          getCustomTableCellValue(documentState, customBinding),
          sheetName
        );
        continue;
      }

      const checkboxMeta = checkboxMap[`${row0},${col0}`];
      if (checkboxMeta) {
        engine.setCellValue(
          row0,
          col0,
          getSheetCheckboxValue(
            documentState,
            sheetName,
            checkboxMeta.index,
            checkboxMeta.defaultChecked
          ),
          sheetName
        );
        continue;
      }

      const variableMeta = getVariableCellMeta(expandedSheet, row0, col0);
      if (variableMeta) {
        const cellText =
          expandedSheet.displayValues?.[row0]?.[col0] ||
          expandedSheet.values?.[row0]?.[col0] ||
          variableMeta.rawValue ||
          '';

        if (isMixedVariableCell(variableMeta, cellText)) {
          // Mixed cells are display/edit composites; formulas rarely depend on them as numbers.
          engine.setCellValue(row0, col0, null, sheetName);
          continue;
        }

        const token = getPrimaryVariableToken(variableMeta, cellText);
        const path = parseTokenPath(token);
        if (!path.length) {
          engine.setCellValue(row0, col0, null, sheetName);
          continue;
        }

        const binding = resolveVariableFieldBinding({
          path,
          appChoices,
          docSource,
          pickedInventory
        });
        const raw =
          binding.owner === 'inventory'
            ? getValueAtPath(pickedInventory, binding.statePath)
            : getValueAtPath(documentState, binding.statePath);

        if (binding.type === 'milestone') {
          engine.setCellValue(row0, col0, coerceSheetValue(raw?.start ?? null), sheetName);
          continue;
        }

        if (binding.fieldKey === 'customEntities' && binding.displayLeaf) {
          const record = getCustomEntityRecord(appChoices, binding.statePath?.[1], raw);
          const leafValue = record?.[binding.displayLeaf];
          if (leafValue !== undefined && leafValue !== null && leafValue !== '') {
            engine.setCellValue(row0, col0, coerceSheetValue(leafValue), sheetName);
            continue;
          }
        }

        engine.setCellValue(row0, col0, coerceSheetValue(toInputValue(raw) || raw), sheetName);
        continue;
      }

      const raw = expandedSheet.values?.[row0]?.[col0];
      if (raw === undefined || raw === null || raw === '') {
        engine.setCellValue(row0, col0, null, sheetName);
        continue;
      }
      if (String(raw).includes('{{') || String(raw).includes('[[')) {
        engine.setCellValue(row0, col0, null, sheetName);
        continue;
      }
      engine.setCellValue(row0, col0, raw, sheetName);
    }
  }
};

/**
 * Push editable / static inputs into HyperFormula for every expanded sheet.
 * Cross-sheet formulas need every sheet's inputs synced, not only the active tab.
 */
export const syncSheetInputsIntoEngine = ({
  engine,
  expandedSheet,
  expandedSheets,
  documentState,
  pickedInventory,
  appChoices,
  docSource
}) => {
  if (!engine?.hf) return;

  const sheets = asExpandedSheetList(
    expandedSheets?.length ? expandedSheets : expandedSheet
  );

  sheets.forEach(sheet => {
    syncOneSheetInputsIntoEngine({
      engine,
      expandedSheet: sheet,
      documentState,
      pickedInventory,
      appChoices,
      docSource
    });
  });
};
